import React, { useState, useEffect } from 'react';
import BBALoader from '../BBALoader';
import { ChevronUp, ChevronDown, Trash2, Plus, ArrowUp } from 'lucide-react';
import EditButton from '../EditButton';
import SaveButton from '../SaveButton';
import { suggestNextVersion } from '../../services/hrCollectionsService';
import {
  fetchContactToBuildTemplate,
  saveContactToBuildTemplate,
  resetContactToBuildTemplate,
} from '../../services/contactToBuildTemplateService';

/**
 * CRM editor for the master "Contact to Build" SOP template (goal, note, and the
 * action/assigned-to timeline). Saving updates contactToBuildTemplate/data, which
 * the portal reads as the default for every account. Rendered in the "Contact to
 * Build" tab of the CRM SOP Templates page.
 */
const ContactToBuildTemplateEditor = ({ darkMode }) => {
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [goal, setGoal] = useState('');
  const [note, setNote] = useState('');
  const [version, setVersion] = useState('1.0');
  const [isCustom, setIsCustom] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editSteps, setEditSteps] = useState([]);
  const [editGoal, setEditGoal] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editVersion, setEditVersion] = useState('1.0');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const t = await fetchContactToBuildTemplate();
    setSteps(t.steps);
    setGoal(t.goal);
    setNote(t.note);
    setVersion(t.version || '1.0');
    setIsCustom(t.isCustom);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = () => {
    setEditSteps(steps.map((s) => ({ ...s })));
    setEditGoal(goal);
    setEditNote(note);
    setEditVersion(version || '1.0');
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  // Bump the template version (1.0 → 1.1 → … → 1.9 → 2.0). Persisted on Save.
  const bumpVersion = () => setEditVersion((v) => suggestNextVersion(v || '1.0'));

  const save = async () => {
    setSaving(true);
    try {
      await saveContactToBuildTemplate({ steps: editSteps, goal: editGoal, note: editNote, version: editVersion });
      setSteps(editSteps);
      setGoal(editGoal);
      setNote(editNote);
      setVersion(editVersion);
      setIsCustom(true);
      setEditing(false);
    } catch (err) {
      console.error('Error saving Contact to Build template:', err);
      alert('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset the Contact to Build template to the built-in default? Accounts that have not customized it will see the default.')) return;
    try {
      await resetContactToBuildTemplate();
      await load();
    } catch (err) {
      console.error('Error resetting Contact to Build template:', err);
    }
  };

  const changeRow = (i, field, val) =>
    setEditSteps(editSteps.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));
  const addRow = () => setEditSteps([...editSteps, { action: '', assignedTo: '' }]);
  const deleteRow = (i) => setEditSteps(editSteps.filter((_, idx) => idx !== i));
  const moveRow = (i, dir) => {
    const next = [...editSteps];
    const swap = dir === 'up' ? i - 1 : i + 1;
    if (swap < 0 || swap >= next.length) return;
    [next[i], next[swap]] = [next[swap], next[i]];
    setEditSteps(next);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <BBALoader size={40} />
      </div>
    );
  }

  const rows = editing ? editSteps : steps;

  return (
    <div className="w-full overflow-x-auto">
      {/* Header: centered title + badge, Edit / Save+Cancel on the right */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0" aria-hidden="true" />
        <div className="flex items-center justify-center gap-3 shrink-0">
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact to Build SOP</h2>
          {editing ? (
            <button
              type="button"
              onClick={bumpVersion}
              title="Click to update to the next version"
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold border transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              v{editVersion}
              <ArrowUp className="w-3 h-3 opacity-60" />
            </button>
          ) : (
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold border ${darkMode ? 'bg-gray-800 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-600 border-gray-200'}`}
            >
              v{version}
            </span>
          )}
        </div>
        <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
          {!editing ? (
            <EditButton onClick={startEdit} />
          ) : (
            <>
              <SaveButton onClick={save} disabled={saving} />
              <button
                type="button"
                onClick={cancel}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Goal */}
      <div className="mb-6">
        {editing ? (
          <div>
            <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Goal</label>
            <textarea
              value={editGoal}
              onChange={(e) => setEditGoal(e.target.value)}
              rows={2}
              className={`w-full resize-y border rounded px-3 py-2 text-sm ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-red-500`}
            />
          </div>
        ) : (
          <p className={`text-base text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Goal: {goal}</p>
        )}
      </div>

      <table className={`w-full border-collapse text-sm sm:text-base ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        <thead>
          <tr className={`border-b-2 ${darkMode ? 'border-red-500 bg-gray-700' : 'border-red-500 bg-gray-100'}`}>
            {editing && (
              <th className={`w-24 py-3 px-2 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} aria-label="Reorder / delete" />
            )}
            <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Action</th>
            <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assigned to</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className={`border-b ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
              {editing && (
                <td className="py-2 px-2 align-middle">
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveRow(index, 'up')}
                      disabled={index === 0}
                      className={`p-1 rounded ${index === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                      title="Move up"
                      aria-label="Move row up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveRow(index, 'down')}
                      disabled={index === editSteps.length - 1}
                      className={`p-1 rounded ${index === editSteps.length - 1 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                      title="Move down"
                      aria-label="Move row down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRow(index)}
                      className="p-1 rounded text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                      title="Delete row"
                      aria-label="Delete row"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              )}
              {editing ? (
                <>
                  <td className="py-2 px-4 align-top">
                    <input
                      type="text"
                      value={row.action}
                      onChange={(e) => changeRow(index, 'action', e.target.value)}
                      className={`w-full min-w-0 border rounded px-2 py-1.5 text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </td>
                  <td className="py-2 px-4 align-top">
                    <input
                      type="text"
                      value={row.assignedTo}
                      onChange={(e) => changeRow(index, 'assignedTo', e.target.value)}
                      className={`w-full min-w-0 border rounded px-2 py-1.5 text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </td>
                </>
              ) : (
                <>
                  <td className="py-3 px-4">{row.action}</td>
                  <td className="py-3 px-4">{row.assignedTo}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="mt-2">
          <button
            type="button"
            onClick={addRow}
            className={`text-red-600 hover:text-red-700 flex items-center text-sm ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded px-2 py-1 -ml-2`}
          >
            <Plus className="w-4 h-4 mr-1" /> Add row
          </button>
        </div>
      )}

      {/* Note */}
      <div className="mt-6">
        {editing ? (
          <div>
            <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Note</label>
            <textarea
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              rows={2}
              className={`w-full resize-y border rounded px-3 py-2 text-sm ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-red-500`}
            />
          </div>
        ) : (
          <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{note}</p>
        )}
      </div>

      {/* Reset to default (only when a custom master exists and not editing) */}
      {isCustom && !editing && (
        <div className="mt-6">
          <button
            type="button"
            onClick={reset}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
};

export default ContactToBuildTemplateEditor;
